import React, { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import { CRow, CCol, CTable, CTableBody } from "@coreui/react-pro";
import moment from "moment";
import { useLocation } from "react-router-dom";
import NoDataTableItem from "../../components/AdminDashboard/NoDataTableItem";
import {
    getCarReportTopicCategories,
    getCarReportTopics,
    createCarReportTopic,
    updateCarReportTopic,
    deleteCarReportTopic,
} from "../../services/carReportTopicAPI";

import { useAppSelector } from "../../app/hooks";
import { selectAuth } from "../../slice/authSlice";

import { CXButton, CXDropDown, CXTextInput } from "../../components/CXForm";
import TagsInput from "../../components/TagInputs";
import Access from "../../components/Access";

import { DeleteIcon, EditIcon } from "../../assets/images";

import { Table, Table as TopicListTable } from "../dataConfig/sharedComponents/Table";
import { Modal } from "../dataConfig/sharedComponents/Modal";

import { AccessType } from "../../constants/roleAccessConstant";
import { emailFormatterValidation } from "../../utils/formValidateHelper";
import "./style/CARAssigneePage.scss";

import { ALL } from "./hooks/useAssigneeSearchAndFilter";

const STORAGE_ASSIGNEE_STATE_KEY = "carReportTracker.assigneePageState";

type PersistedAssigneeState = {
    appliedKeyword: string;
    selectedAirline: string;
    selectedCategory: string;
};

const readPersistedAssigneeState = (key: string): PersistedAssigneeState | null => {
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed) return null;

        return {
            appliedKeyword: String(parsed.appliedKeyword ?? ""),
            selectedAirline: String(parsed.selectedAirline ?? ""),
            selectedCategory: String(parsed.selectedCategory ?? ""),
        };
    } catch {
        return null;
    }
};

const writePersistedAssigneeState = (key: string, state: PersistedAssigneeState) => {
    try {
        sessionStorage.setItem(key, JSON.stringify(state));
    } catch {
        // ignore
    }
};

const BASE_ZINDEX = 1100;

type ModalType = "" | "add" | "edit" | "delete";

type Topic = {
    _id?: string;
    Airline?: string;
    Category?: string;
    Subject?: string;
    Emails?: string[];
    AssigneeType?: "GROUP" | "ASSIGNEE_LEAD";
    AssigneeName?: string;
    galacxyId?: string;
    updatedAt?: string;
};

type ModalInfo = {
    _id?: string;
    Airline: string;
    Category: string;
    Subject: string;
    Emails: string[];
    AssigneeType?: "GROUP" | "ASSIGNEE_LEAD";
    AssigneeName?: string;
    galacxyId?: string;
};

const initialModalInfo: ModalInfo = {
    Airline: "",
    Category: "",
    Subject: "",
    Emails: [],
    AssigneeType: undefined,
    AssigneeName: "",
};

const modalTitleMap: Record<Exclude<ModalType, "">, string> = {
    add: "Add new assignee",
    edit: "Edit assignee",
    delete: "Delete assignee",
};

const actionButtonTextMap: Record<Exclude<ModalType, "">, string> = {
    add: "Add new",
    edit: "Save",
    delete: "Confirm",
};

const inputFields = ["Airline", "Category", "Subject", "AssigneeType", "AssigneeName", "Emails"];

const FormItem = ({ label, children }: { label: string; children: React.ReactNode }) => {
    return (
        <CRow>
            <CCol xs={6}>
                <span className="modal-section-title">{label}</span>
            </CCol>
            <CCol xs={6}>{children}</CCol>
        </CRow>
    );
};

const ErrorPlaceholder = ({ errorMessage }: { errorMessage: string }) => {
    return <div className="errorPlaceholder">{errorMessage}</div>;
};

const getCategoryOptions = (categoryList: any = []) => {
    return (categoryList ?? []).map((item: any) => ({
        value: item,
        text: item,
    }));
};

const normalizeValue = (v: any) => {
    if (typeof v === "string") return v;

    if (v && typeof v === "object") {
        if (typeof v.value === "string") return v.value;
        if (typeof v.text === "string") return v.text;
        if (typeof v.label === "string") return v.label;
    }
    return "";
};

const normalizeAssigneeName = (value: any) => String(value ?? "").toUpperCase();

const ASSIGNEE_TYPE_LABELS: Record<string, string> = {
    GROUP: "Group",
    ASSIGNEE_LEAD: "Lead",
};

const formatAssigneeType = (t?: string) => {
    if (!t) return "-";
    return ASSIGNEE_TYPE_LABELS[t] ?? t;
};

const extractTopicList = (res: any): Topic[] => {
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.topicList)) return res.topicList;
    if (Array.isArray(res?.data?.topicList)) return res.data.topicList;
    return [];
};

const extractCategoryList = (res: any): string[] => {
    if (Array.isArray(res?.categoryList)) return res.categoryList;
    if (Array.isArray(res?.data?.categoryList)) return res.data.categoryList;
    if (Array.isArray(res?.data)) return res.data;
    return [];
};

const withRowKey = (list: any[]) =>
    (list ?? []).map((i: any) => ({
        ...i,
        key: i?._id ?? `${i?.Airline ?? ""}-${i?.Category ?? ""}-${i?.Subject ?? ""}`,
    }));

const toBackendValue = (v: any) => {
    const s = String(v ?? "").trim();
    if (!s) return "";
    if (s === ALL) return "";
    if (s.toUpperCase() === "ALL") return "";
    return s;
};

const CARReportTrackerAssigneePage = () => {
    const { pathname: routePath } = useLocation();
    const storageKey = `${STORAGE_ASSIGNEE_STATE_KEY}:${routePath}`;
    const restored = useMemo(() => readPersistedAssigneeState(storageKey), [storageKey]);

    const { userName } = useAppSelector(selectAuth);

    const isUO = false;

    const [categoryList, setCategoryList] = useState<string[]>([]);

    const [enteredFields, setEnteredFields] = useState<string[]>([]);
    const [modalType, setModalType] = useState<ModalType>("");
    const [modalInfo, setModalInfo] = useState<ModalInfo>(initialModalInfo);

    const [isAirlineValid, setIsAirlineValid] = useState(true);
    const [isCategoryValid, setIsCategoryValid] = useState(true);
    const [isSubjectValid, setIsSubjectValid] = useState(true);
    const [isAssigneeTypeValid, setIsAssigneeTypeValid] = useState(true);
    const [isAssigneeNameValid, setIsAssigneeNameValid] = useState(true);
    const [isEmailsValid, setIsEmailsValid] = useState(true);

    const [uiKeyword, setUiKeyword] = useState(() => restored?.appliedKeyword ?? "");
    const [appliedKeyword, setAppliedKeyword] = useState(() => restored?.appliedKeyword ?? "");

    const [selectedAirline, setSelectedAirline] = useState(() => (restored?.selectedAirline ?? ""));
    const [selectedCategory, setSelectedCategory] = useState(() => (restored?.selectedCategory ?? ""));

    const [topicList, setTopicList] = useState<Topic[]>([]);

    const prevUiKeywordRef = useRef("");

    const airlineOptions = useMemo(
        () => [
            { value: ALL, text: "ALL" },
            { value: "CPA", text: "CPA" },
            { value: "AHK", text: "AHK" },
        ],
        [],
    );

    const categoryOptions = useMemo(() => {
        return [{ value: ALL, text: "ALL" }, ...getCategoryOptions(categoryList)];
    }, [categoryList]);

    const modalAirlineOptions = useMemo(
        () => [
            { value: "CPA", text: "CPA" },
            { value: "AHK", text: "AHK" },
        ],
        [],
    );

    const getCategories = useCallback(async () => {
        try {
            const res = await getCarReportTopicCategories(isUO);
            const list = extractCategoryList(res);
            setCategoryList(list ?? []);
        } catch {
            // ignore
        }
    }, [isUO]);

    const topicsSeqRef = useRef(0);
    const fetchTopicsFromApi = useCallback(
        async (keyword: string, category: string, airline: string) => {
            const seq = ++topicsSeqRef.current;
            try {
                const res = await getCarReportTopics(keyword, category, airline, isUO);
                if (seq !== topicsSeqRef.current) return;
                setTopicList(extractTopicList(res) ?? []);
            } catch {
                if (seq !== topicsSeqRef.current) return;
                setTopicList([]);
            }
        },
        [isUO],
    );

    useEffect(() => {
        void getCategories();
    }, [getCategories]);

    useEffect(() => {
        const kw = (appliedKeyword ?? "").trim();
        const cat = toBackendValue(selectedCategory);
        const air = toBackendValue(selectedAirline);

        void fetchTopicsFromApi(kw, cat, air);
    }, [appliedKeyword, selectedCategory, selectedAirline, fetchTopicsFromApi]);

    const tableDataSigRef = useRef<string>("");
    const tableDataRef = useRef<any[]>([]);

    const buildRowSig = (t: any) => {
        const id = String(t?._id ?? "");
        const airline = String(t?.Airline ?? "");
        const category = String(t?.Category ?? "");
        const subject = String(t?.Subject ?? "");
        const assigneeType = String(t?.AssigneeType ?? "");
        const assigneeName = String(t?.AssigneeName ?? "");
        const emails = Array.isArray(t?.Emails) ? t.Emails.join(",") : String(t?.Emails ?? "");
        const galacxyId = String(t?.galacxyId ?? "");
        const updatedAt = String(t?.updatedAt ?? "");
        return `${id}|${airline}|${category}|${subject}|${assigneeType}|${assigneeName}|${emails}|${galacxyId}|${updatedAt}`;
    };

    const tableDataSource = useMemo(() => {
        const list = Array.isArray(topicList) ? topicList : [];
        const sigParts = list.map(buildRowSig);
        sigParts.sort();
        const sig = `${sigParts.length}::${sigParts.join("||")}`;

        if (sig === tableDataSigRef.current) {
            return tableDataRef.current;
        }

        tableDataSigRef.current = sig;
        tableDataRef.current = withRowKey(list);
        return tableDataRef.current;
    }, [topicList]);

    const checkDataValidation = (data: ModalInfo = modalInfo) => {
        const okAirline = (data.Airline ?? "").trim().length > 0;
        const okCategory = (data.Category ?? "").trim().length > 0;
        const okSubject = (data.Subject ?? "").trim().length > 0;
        const okAssigneeType = !!data.AssigneeType;
        const okAssigneeName = (data.AssigneeName ?? "").trim().length > 0;
        const okEmails =
            (data.Emails ?? []).length > 0 &&
            data.Emails.every((item: string) => emailFormatterValidation(String(item ?? "").trim()));

        setIsAirlineValid(okAirline);
        setIsCategoryValid(okCategory);
        setIsSubjectValid(okSubject);
        setIsAssigneeTypeValid(okAssigneeType);
        setIsAssigneeNameValid(okAssigneeName);
        setIsEmailsValid(okEmails);

        return okAirline && okCategory && okSubject && okAssigneeType && okAssigneeName && okEmails;
    };

    const reloadCurrentDataset = useCallback(() => {
        const kw = (appliedKeyword ?? "").trim();
        const cat = toBackendValue(selectedCategory);
        const air = toBackendValue(selectedAirline);
        return fetchTopicsFromApi(kw, cat, air);
    }, [appliedKeyword, selectedCategory, selectedAirline, fetchTopicsFromApi]);

    const runSearch = useCallback(() => {
        const next = (uiKeyword ?? "").trim();
        const prev = (appliedKeyword ?? "").trim();

        setAppliedKeyword(next);

        if (next === prev) {
            const cat = toBackendValue(selectedCategory);
            const air = toBackendValue(selectedAirline);
            void fetchTopicsFromApi(next, cat, air);
        }
    }, [uiKeyword, appliedKeyword, selectedCategory, selectedAirline, fetchTopicsFromApi]);

    useEffect(() => {
        const prev = prevUiKeywordRef.current.trim();
        const curr = (uiKeyword ?? "").trim();
        prevUiKeywordRef.current = uiKeyword ?? "";

        if (prev !== "" && curr === "") {
            if ((appliedKeyword ?? "").trim() !== "") {
                setAppliedKeyword("");
            }
        }
    }, [uiKeyword, appliedKeyword]);

    const sanitizeModalInfo = (m: ModalInfo): ModalInfo => ({
        ...m,
        Airline: (m.Airline ?? "").trim(),
        Category: (m.Category ?? "").trim(),
        Subject: (m.Subject ?? "").trim(),
        AssigneeName: normalizeAssigneeName((m.AssigneeName ?? "").trim()),
        Emails: (m.Emails ?? []).map(e => String(e ?? "").trim()).filter(Boolean),
    });

    const handleAddTopic = async () => {
        try {
            setEnteredFields(inputFields);

            const cleaned = sanitizeModalInfo(modalInfo);

            if (!checkDataValidation(cleaned)) return;

            await createCarReportTopic(
                {
                    Airline: cleaned.Airline,
                    Category: cleaned.Category,
                    Subject: cleaned.Subject,
                    Emails: cleaned.Emails,
                    AssigneeType: cleaned.AssigneeType,
                    AssigneeName: cleaned.AssigneeName,
                    galacxyId: userName,
                },
                isUO,
            );

            setModalType("");
            await reloadCurrentDataset();
        } catch {
            // ignore
        }
    };

    const handleEditTopic = async () => {
        try {
            setEnteredFields(inputFields);
            const cleaned = sanitizeModalInfo(modalInfo);

            if (!checkDataValidation(cleaned)) return;
            if (!modalInfo._id) return;

            await updateCarReportTopic(
                {
                    _id: modalInfo._id,
                    Airline: cleaned.Airline,
                    Category: cleaned.Category,
                    Subject: cleaned.Subject,
                    Emails: cleaned.Emails,
                    AssigneeType: cleaned.AssigneeType,
                    AssigneeName: cleaned.AssigneeName,
                    galacxyId: userName,
                },
                isUO,
            );

            setModalType("");
            await reloadCurrentDataset();
        } catch {
            // ignore
        }
    };

    const handleDeleteTopic = async () => {
        try {
            if (!modalInfo._id) return;

            await deleteCarReportTopic(modalInfo._id, isUO);

            setModalType("");
            await reloadCurrentDataset();
        } catch {
            // ignore
        }
    };

    const modalActionMap: Record<Exclude<ModalType, "">, () => Promise<void>> = {
        add: handleAddTopic,
        edit: handleEditTopic,
        delete: handleDeleteTopic,
    };

    const normalizeAssigneeType = (t: any) => {
        const s = String(t ?? '').trim().toUpperCase();
        if (s === 'GROUP') return 'GROUP';
        if (s === 'ASSIGNEE_LEAD') return 'ASSIGNEE_LEAD';
        return undefined;
    };

    const handleModalOpen = useCallback((type: ModalType, info: any = initialModalInfo) => {
        if (!type) return;

        setModalType(type);
        const isAdd = type === "add";

        setModalInfo({
            Airline: normalizeValue(info.Airline) || "",
            Category: normalizeValue(info.Category) || "",
            Subject: info.Subject || "",
            Emails: info.Emails || [],
            AssigneeType: normalizeAssigneeType(info.AssigneeType),
            AssigneeName: info.AssigneeName || "",
            _id: info._id || "",
            galacxyId: info.galacxyId || "",
        });

        setEnteredFields(isAdd ? [] : inputFields);
    }, []);

    const handleModalClose = () => {
        setModalType("");
        setModalInfo(initialModalInfo);
        setEnteredFields([]);

        setIsAirlineValid(true);
        setIsCategoryValid(true);
        setIsSubjectValid(true);
        setIsAssigneeTypeValid(true);
        setIsAssigneeNameValid(true);
        setIsEmailsValid(true);
    };

    const handleModalInfoChange = (field: string, value: any) => {
        if (!enteredFields.includes(field)) setEnteredFields([...enteredFields, field]);
        const normalizedValue = field === "AssigneeName" ? normalizeAssigneeName(value) : value;
        setModalInfo((prev) => ({ ...prev, [field]: normalizedValue }));
    };

    useEffect(() => {
        if (enteredFields.includes("Airline")) setIsAirlineValid(modalInfo.Airline?.length > 0);
        if (enteredFields.includes("Category")) setIsCategoryValid(modalInfo.Category?.length > 0);
        if (enteredFields.includes("Subject")) setIsSubjectValid(modalInfo.Subject?.length > 0);
        if (enteredFields.includes("AssigneeType")) setIsAssigneeTypeValid(!!modalInfo.AssigneeType);
        if (enteredFields.includes("AssigneeName")) setIsAssigneeNameValid((modalInfo.AssigneeName ?? "").length > 0);
        if (enteredFields.includes("Emails")) {
            setIsEmailsValid(modalInfo.Emails.length > 0 && modalInfo.Emails.every((item: string) => emailFormatterValidation(item)));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalInfo]);

    useLayoutEffect(() => {
        writePersistedAssigneeState(storageKey, {
            appliedKeyword: (appliedKeyword ?? "").trim(),
            selectedAirline: String(selectedAirline ?? ALL),
            selectedCategory: String(selectedCategory ?? ALL),
        });
    }, [storageKey, appliedKeyword, selectedAirline, selectedCategory]);

    const handleAirlineFilterChange = (v: any) => {
        const raw = normalizeValue(v);
        const nextAirline = raw ? raw : ALL;
        if (nextAirline === selectedAirline) return;
        setSelectedAirline(nextAirline);
    };

    const handleCategoryFilterChange = (v: any) => {
        const raw = normalizeValue(v);
        const nextCategory = raw ? raw : ALL;
        if (nextCategory === selectedCategory) return;
        setSelectedCategory(nextCategory);
    };

    const getAssigneeNameColumnTitle = (type?: ModalInfo["AssigneeType"]): string => {
        if (!type) return "Assignee name";
        if (type === "GROUP") return "Assignee group name";
        if (type === "ASSIGNEE_LEAD") return "Assignee lead name";
        return "Assignee name";
    };

    const generateModalBody = () => {
        if (modalType === "add" || modalType === "edit") {
            const assigneeNameColumnTitle = getAssigneeNameColumnTitle(modalInfo.AssigneeType);

            return (
                <>
                    <FormItem label={"CAR report information"}>
                        <CXDropDown
                            zIndex={BASE_ZINDEX - 1}
                            value={modalInfo.Airline}
                            placeholder={"Airline"}
                            onChange={(v) => handleModalInfoChange("Airline", normalizeValue(v))}
                            options={modalAirlineOptions}
                            className={!isAirlineValid ? "errorBorder" : ""}
                        />
                        {!isAirlineValid && <ErrorPlaceholder errorMessage={"Airline may not be blank."} />}

                        <CXDropDown
                            zIndex={BASE_ZINDEX - 2}
                            value={modalInfo.Category}
                            placeholder={"Category"}
                            onChange={(v) => handleModalInfoChange("Category", normalizeValue(v))}
                            options={getCategoryOptions(categoryList)}
                            className={!isCategoryValid ? "errorBorder" : ""}
                        />
                        {!isCategoryValid && <ErrorPlaceholder errorMessage={"Category may not be blank."} />}

                        <CXTextInput
                            key={"input"}
                            placeholder={"Subject"}
                            value={modalInfo.Subject}
                            onChange={(v) => handleModalInfoChange("Subject", v)}
                            errMsg={"Subject may not be blank."}
                            additionalValidation={() => isSubjectValid}
                            className={!isSubjectValid ? "errorBorder" : ""}
                        />
                    </FormItem>

                    <hr className="solid divider" />

                    <FormItem label={"Assignee information"}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                                <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <input
                                        type="radio"
                                        name="assigneeType"
                                        checked={modalInfo.AssigneeType === "GROUP"}
                                        onChange={() => handleModalInfoChange("AssigneeType", "GROUP")}
                                    />
                                    <span>Assignee Group</span>
                                </label>

                                <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <input
                                        type="radio"
                                        name="assigneeType"
                                        checked={modalInfo.AssigneeType === "ASSIGNEE_LEAD"}
                                        onChange={() => handleModalInfoChange("AssigneeType", "ASSIGNEE_LEAD")}
                                    />
                                    <span>Assignee Lead</span>
                                </label>
                            </div>

                            {!isAssigneeTypeValid && <ErrorPlaceholder errorMessage={"Assignee type may not be blank."} />}

                            <CXTextInput
                                key={"assigneeName"}
                                placeholder={assigneeNameColumnTitle}
                                value={modalInfo.AssigneeName ?? ""}
                                onChange={(v) => handleModalInfoChange("AssigneeName", v)}
                                errMsg={"Assignee may not be blank."}
                                additionalValidation={() => isAssigneeNameValid}
                                className={!isAssigneeNameValid ? "errorBorder" : ""}
                            />
                        </div>
                    </FormItem>

                    <hr className="solid divider" />

                    <FormItem label={"Email address"}>
                        <TagsInput
                            className={"tagsInputContainer"}
                            placeholder={'Email address (Use “enter” key for multiple input)'}
                            tagList={modalInfo.Emails}
                            onChangeField={(v) => handleModalInfoChange("Emails", v)}
                            errMsg={"Please enter valid email addresses."}
                            additionalValidation={() => isEmailsValid}
                        />
                    </FormItem>
                </>
            );
        }

        if (modalType === "delete") {
            const emailText = Array.isArray(modalInfo.Emails) ? modalInfo.Emails.join(", ") : "";

            return (
                <div>
                    <div style={{ marginBottom: "20px" }}>
                        <span>Are you sure to delete this assignee? All details of this assignee will be removed.</span>
                    </div>

                    <div>
                        <Table
                            hover={false}
                            columns={[
                                { title: "Airline", dataIndex: "Airline", key: "Airline" },
                                { title: "Category", dataIndex: "Category", key: "Category" },
                                { title: "Subject", dataIndex: "Subject", key: "Subject" },
                                {
                                    title: "Assignee type",
                                    dataIndex: "AssigneeType",
                                    key: "AssigneeType",
                                    render: (row: any) => formatAssigneeType(row?.AssigneeType),
                                },
                                {
                                    title: getAssigneeNameColumnTitle(modalInfo.AssigneeType),
                                    dataIndex: "AssigneeName",
                                    key: "AssigneeName",
                                    render: (row: any) => (row?.AssigneeName || "").trim() || "-",
                                },
                                { title: "Email address", dataIndex: "EmailText", key: "EmailText" },
                            ]}
                            dataSource={[
                                {
                                    Airline: modalInfo.Airline,
                                    Category: modalInfo.Category,
                                    Subject: modalInfo.Subject,
                                    AssigneeType: modalInfo.AssigneeType,
                                    AssigneeName: modalInfo.AssigneeName,
                                    EmailText: emailText,
                                },
                            ]}
                        />
                    </div>
                </div>
            );
        }

        return null;
    };

    const isEmptyTable = Array.isArray(topicList) && topicList.length === 0;

    const topicColumns = useMemo(
        () => [
            { title: "Airline", dataIndex: "Airline", key: "Airline", width: 90, isSortable: true },
            { title: "Category", dataIndex: "Category", key: "Category", width: 160, isSortable: true },
            { title: "Subject", dataIndex: "Subject", key: "Subject", width: 220, isSortable: true },
            {
                title: "Assignee type",
                dataIndex: "AssigneeType",
                key: "AssigneeType",
                width: 140,
                isSortable: true,
                render: (row: any) => formatAssigneeType(row?.AssigneeType),
            },
            {
                title: "Assignee name",
                dataIndex: "AssigneeName",
                key: "AssigneeName",
                width: 200,
                isSortable: true,
                render: (data: any) => data?.AssigneeName ?? "-",
            },
            { title: "Emails", dataIndex: "Emails", key: "Emails", width: 280, isSortable: true },
            {
                title: "Last updated",
                dataIndex: "updatedAt",
                key: "updatedAt",
                width: 190,
                isSortable: true,
                render: (row: any) => (
                    <>
                        {row?.galacxyId && (
                            <CRow>
                                <span>{row.galacxyId}</span>
                            </CRow>
                        )}
                        {row?.updatedAt && (
                            <CRow>
                                <span>{moment(row.updatedAt).format("DD MMM YYYY HH:mm")}</span>
                            </CRow>
                        )}
                    </>
                ),
            },
            {
                title: "",
                dataIndex: "Action",
                key: "Action",
                width: 120,
                render: (row: any) => (
                    <div className="assignee-action-icons">
                        <Access route={routePath} access={[AccessType.UPDATE]}>
                            <EditIcon className="clickable-icon" height={40} width={40} onClick={() => handleModalOpen("edit", row)} />
                        </Access>

                        <Access route={routePath} access={[AccessType.DELETE]}>
                            <DeleteIcon className="clickable-icon" height={40} width={40} onClick={() => handleModalOpen("delete", row)} />
                        </Access>
                    </div>
                ),
            },
        ],
        [routePath, handleModalOpen],
    );

    return (
        <div className="carReportTopicPage">
            <CRow className="searchBar justify-content-between">
                <CCol className="d-flex">
                    <CCol sm="auto">
                        <div className={"searchBox"}>
                            <CXTextInput
                                placeholder={"Search by subject / assignee / email address"}
                                value={uiKeyword}
                                onChange={(v) => setUiKeyword(v)}
                                zIndex={1029}
                            />
                        </div>
                    </CCol>

                    <CCol sm="auto">
                        <div className={"searchButton"}>
                            <CXButton text={"Search"} onClick={runSearch} fontSize={"16px"} width={"100%"} />
                        </div>
                    </CCol>
                </CCol>

                <CCol className="d-flex flex-row-reverse">
                    <Access route={routePath} access={[AccessType.UPDATE]}>
                        <CXButton
                            onClick={() => handleModalOpen("add")}
                            text={"Add new assignee"}
                            outline={true}
                            fontSize={"14px"}
                            width="162px"
                        />
                    </Access>
                </CCol>
            </CRow>

            <CRow style={{ minHeight: 56, marginBottom: 8, alignItems: "center" }}>
                <div className="d-flex" style={{ gap: 8, alignItems: "center", flexWrap: "nowrap" }}>
                    <CCol sm={1} className="ps-0" style={{ minWidth: "120px" }}>
                        <CXDropDown
                            itemWidth="120px"
                            value={selectedAirline}
                            placeholder={"Airline"}
                            onChange={handleAirlineFilterChange}
                            options={airlineOptions}
                            zIndex={1001}
                        />
                    </CCol>

                    <CCol sm={1} className="ps-0" style={{ minWidth: "160px" }}>
                        <CXDropDown
                            itemWidth="160px"
                            value={selectedCategory}
                            placeholder={"Category"}
                            onChange={handleCategoryFilterChange}
                            options={categoryOptions}
                            zIndex={1000}
                        />
                    </CCol>
                </div>
            </CRow>

            <div className={"section tableContainer"} style={{ position: "relative" }}>
                <TopicListTable
                    columns={topicColumns}
                    dataSource={tableDataSource}
                    dynamicSortIndicator
                />

                <CTable hover responsive className="align-middle text-center">
                    {isEmptyTable ? (
                        <CTableBody>
                            <NoDataTableItem colSpan={11} message={"There is no CAR Assignee data"} />
                        </CTableBody>
                    ) : null}
                </CTable>
            </div>

            <Modal
                show={!!modalType}
                onHide={handleModalClose}
                title={modalType ? modalTitleMap[modalType] : ""}
                className="carReportTopicPageModal"
                onAction={async () => {
                    if (!modalType) return;
                    const fn = modalActionMap?.[modalType];
                    if (typeof fn === "function") await fn();
                }}
                actionButtonText={modalType ? actionButtonTextMap[modalType] : ""}
            >
                {generateModalBody()}
            </Modal>
        </div>
    );
};

export default CARReportTrackerAssigneePage;
